import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  getCurrentUserRecentSearches, 
  addPatientToRecentSearches, 
  clearCurrentUserRecentSearches 
} from '../services/recentSearchService';
// Removed Redux auth selectors - now using useAuth() hook
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook for managing facility-specific recent searches functionality
 * Provides functions to add patients to recent searches, load recent searches,
 * and clear recent searches. Automatically handles authentication state and facility changes.
 */
export const useRecentSearches = (facilityId) => {
  const { user } = useAuth();
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);



  // Load recent searches for the current user in the specified facility
  const loadRecentSearches = useCallback(async () => {
    if (!user?.uid || !facilityId) {
      setRecentSearches([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const searches = await getCurrentUserRecentSearches(facilityId, user.uid);
      setRecentSearches(searches);
    } catch (error) {
      console.error('Error loading recent searches:', error);
      setError(error.message);
      // Don't show error to user for recent searches - it's a convenience feature
    } finally {
      setLoading(false);
    }
  }, [user?.uid, facilityId]);

  // Add a patient to recent searches in the specified facility
  const addToRecentSearches = useCallback(async (patient) => {
    if (!user?.uid || !facilityId) {
      console.warn('Cannot add to recent searches: user not authenticated or no facility ID');
      return;
    }

    try {
      await addPatientToRecentSearches(patient, facilityId, user.uid);
      // Reload recent searches to show the updated list
      await loadRecentSearches();
    } catch (error) {
      console.error('Error adding patient to recent searches:', error);
      // Don't throw - this is a convenience feature
    }
  }, [user?.uid, facilityId, loadRecentSearches]);

  // Clear all recent searches in the specified facility
  const clearRecentSearches = useCallback(async () => {
    if (!user?.uid || !facilityId) {
      console.warn('Cannot clear recent searches: user not authenticated or no facility ID');
      return;
    }

    try {
      await clearCurrentUserRecentSearches(facilityId, user.uid);
      setRecentSearches([]);
      setError(null);
    } catch (error) {
      console.error('Error clearing recent searches:', error);
      setError(error.message);
      throw error;
    }
  }, [user?.uid, facilityId]);

  // Navigate to patient and add to recent searches in the specified facility
  const navigateToPatient = useCallback(async (patient, navigate) => {
    try {
      // Add to recent searches first
      await addToRecentSearches(patient);
    } catch (error) {
      console.error('Error adding patient to recent searches:', error);
      // Don't block navigation if recent search fails
    }
    
    // Navigate to patient details
    navigate(`/patients/${patient.id}`);
  }, [addToRecentSearches]);

  // Navigate to patient by ID and add to recent searches in the specified facility
  const navigateToPatientById = useCallback(async (patientId, patientData, navigate) => {
    if (!patientData) {
      console.warn('No patient data provided for recent search');
      navigate(`/patients/${patientId}`);
      return;
    }

    try {
      // Add to recent searches first
      await addToRecentSearches(patientData);
    } catch (error) {
      console.error('Error adding patient to recent searches:', error);
      // Don't block navigation if recent search fails
    }
    
    // Navigate to patient details
    navigate(`/patients/${patientId}`);
  }, [addToRecentSearches]);

  // Load recent searches when user or facility changes
  useEffect(() => {
    // Only load recent searches if we have both user and facilityId
    if (user?.uid && facilityId) {
      loadRecentSearches();
    } else {
      // Clear recent searches if no facilityId
      setRecentSearches([]);
    }
  }, [user?.uid, facilityId, loadRecentSearches]);

  return {
    recentSearches,
    loading,
    error,
    loadRecentSearches,
    addToRecentSearches,
    clearRecentSearches,
    navigateToPatient,
    navigateToPatientById
  };
};
